import axiosInstance from './axiosConfig';

export interface CodeListItem {
    code: string;
    description: string;
    codeType: string;
    category?: string;
    billable?: boolean;
}

export interface CodeListResponse {
    codes: CodeListItem[];
    hasMore: boolean;
    total: number;
    page: number;
}

// ✅ Frontend in-memory cache — repeat queries instant honge
const memCache = new Map<string, { data: any; exp: number }>();

const getCached = (key: string) => {
    const item = memCache.get(key);
    if (!item) return null;
    if (Date.now() > item.exp) { memCache.delete(key); return null; }
    return item.data;
};

const setCached = (key: string, data: any, ttlMs: number) => {
    memCache.set(key, { data, exp: Date.now() + ttlMs });
};

export const codeApi = {
    getStats: async () => {
        const key = 'stats';
        const cached = getCached(key);
        if (cached) return cached;
        const response = await axiosInstance.get('/codes/stats');
        setCached(key, response.data, 60 * 60 * 1000); // 1 hour
        return response.data;
    },

    // Normalize response — backend Code (uppercase) → frontend code (lowercase)
    _normalize: (data: any): CodeListResponse => {
        const codes = (data.codes ?? data.results ?? []).map((item: any) => ({
            code: item.code ?? item.Code ?? '',
            description: item.description ?? item.Description ?? '',
            codeType: item.codeType ?? item.CodeType ?? '',
            category: item.category ?? item.Category ?? item.section ?? item.Section,
            billable: item.billable ?? item.Billable,
        }));
        return { codes, hasMore: data.hasMore ?? false, total: data.total ?? 0, page: data.page ?? 1 };
    },

    listCodes: async (
        codeType: string,
        page = 1,
        pageSize = 50,
        search = ''
    ): Promise<CodeListResponse> => {
        const key = `list:${codeType}:${page}:${pageSize}:${search}`;
        const cached = getCached(key);
        if (cached) return cached as CodeListResponse;
        const params: any = { page, pageSize };
        if (search) params.search = search;
        const response = await axiosInstance.get(`/codes/list/${codeType}`, { params });
        const normalized = codeApi._normalize(response.data);
        const ttl = search ? 5 * 60 * 1000 : 15 * 60 * 1000;
        setCached(key, normalized, ttl);
        return normalized;
    },

    getUpdateLogs: async (limit = 20) => {
        const response = await axiosInstance.get('/codes/update-logs', { params: { limit } });
        return response.data;
    },
};